import React, { useMemo } from 'react';
import './TypesShareBarChart.scss';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { t } from 'i18next';
import CustomBarChart from '../../../../shared/components/chart/custom-bar-chart/CustomBarChart';
import {
  getVerticalBarChartSize,
  prepareAndGetVerticalBarOptions,
} from '../../../../shared/components/chart/custom-bar-chart/helpers/CustomBarChartHelper';
import ChartTooltip from '../../../../shared/components/chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { renderToStaticMarkup } from 'react-dom/server';
import { useChartColorPalette } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseChartColorPalette';
import { useLegendState } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseLegendState';

interface TypesShareBarChartProps {
  typesShareMatrix: string[][] | undefined | null;
}

const TypesShareBarChart: React.FC<TypesShareBarChartProps> = ({ typesShareMatrix }) => {
  const hasData = Array.isArray(typesShareMatrix) && typesShareMatrix.length > 1;

  const initialLabels = useMemo(
    () => (hasData ? typesShareMatrix!.slice(1).map((d) => d[0]) : []),
    [typesShareMatrix, hasData]
  );

  const { legendStateMap, handleLegendClick } = useLegendState(initialLabels);

  const legendData = useChartColorPalette(typesShareMatrix, hasData);

  const colorMap = useMemo(
    () => new Map(legendData.map((item) => [item.label, item.color])),
    [legendData]
  );

  const filteredData = useMemo(
    () => (hasData ? typesShareMatrix!.filter((row) => legendStateMap.get(row[0]) !== false) : []),
    [typesShareMatrix, legendStateMap, hasData]
  );

  const tooltipFormatter = (params: any): string => {
    return renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={params} />);
  };

  const chartOptions = useMemo(
    () => (hasData ? prepareAndGetVerticalBarOptions(tooltipFormatter, filteredData, colorMap) : null),
    [filteredData, colorMap, hasData]
  );

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('sourceTypesShare')}
          <CustomTooltip tooltipText="sourceTypesShareTooltip" addMargin={false} />
        </div>
      </div>
      {!hasData ? (
        <div className="stats-box stats-box--bar-chart stats-box--empty">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--bar-chart">
          <div className="stats-box__content">
            <CustomBarChart
              customEChartsOption={{ eChartsOption: chartOptions, legendData }}
              legendStateMap={legendStateMap}
              onLegendClick={handleLegendClick}
              chartStyle={getVerticalBarChartSize}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TypesShareBarChart;
